import { ReactElement, useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { Button, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import type { ManagedUserDTO, OrganisationDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationsQueryKey,
  getGetUsersQueryKey,
  useDeleteMembership,
  useUpdateMembership,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { DialogBase } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { organisationLabel } from '@utils'
import AddMembershipForm from './components/AddMembershipForm'

interface Props {
  user: ManagedUserDTO
  organisations: OrganisationDTO[]
  onClose: () => void
}

const ManageMembershipsDialog = ({ user, organisations, onClose }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const memberships = useMemo(() => user.organisations || [], [user.organisations])

  const memberCountByOrganisation = useMemo(
    () => new Map(organisations.map(organisation => [organisation.guid, organisation.user_count])),
    [organisations],
  )

  const joinableOrganisations = useMemo(() => {
    const joined = new Set(memberships.map(organisation => organisation.guid))

    return organisations.filter(organisation => !joined.has(organisation.guid))
  }, [organisations, memberships])

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries(getGetUsersQueryKey()),
      queryClient.invalidateQueries(getGetOrganisationsQueryKey()),
    ])
  }

  const { mutate: updateMembership, isLoading: isUpdatingRole } = useUpdateMembership<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Rolle ändern',
        description: 'Vergibt oder entzieht die Administratorrolle oder Planungsverantwortung',
      },
      onSuccess: invalidate,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der Rolle'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: deleteMembership } = useDeleteMembership<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Organisation entfernen',
        description: 'Beendet die Mitgliedschaft des Nutzers',
      },
      onSuccess: async () => {
        await invalidate()
        enqueueSnackbar('Mitgliedschaft beendet', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Entfernen der Organisation'), {
          variant: 'error',
        })
      },
    },
  })

  return (
    <DialogBase
      open
      onClose={onClose}
      title={`Organisationen von ${user.name || user.email || user.id}`}
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    >
      <Box display="flex" flexDirection="column" gap={2} pt={1} data-cy="membership-list">
        {memberships.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Der Nutzer gehört noch keiner Organisation an.
          </Typography>
        )}
        {memberships.map(organisation => {
          // Both ends of a membership have to survive its removal, and the
          // backend refuses either way with a 409. Say so up front rather than
          // letting the user find out from an error.
          const blockedReason =
            memberships.length <= 1
              ? 'Einzige Organisation des Nutzers – ohne Organisation kann er nichts tun. Stattdessen den Nutzer löschen.'
              : (memberCountByOrganisation.get(organisation.guid) ?? 0) <= 1
              ? 'Letztes Mitglied der Organisation kann nicht entfernt werden'
              : ''

          return (
            <Box
              key={organisation.guid}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="body2">{organisationLabel(organisation)}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={organisation.is_admin}
                      // The switch reflects server state, so it springs back
                      // until the refetch lands. Locking it until then stops a
                      // second toggle racing the first.
                      disabled={isUpdatingRole}
                      onChange={event =>
                        updateMembership({
                          data: {
                            user_id: user.id,
                            organisation_guid: organisation.guid,
                            is_admin: event.target.checked,
                            is_responsible: organisation.is_responsible,
                          },
                        })
                      }
                      data-cy="switch-membership-admin"
                    />
                  }
                  label="Admin"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={organisation.is_responsible}
                      disabled={isUpdatingRole}
                      onChange={event =>
                        updateMembership({
                          data: {
                            user_id: user.id,
                            organisation_guid: organisation.guid,
                            is_admin: organisation.is_admin,
                            is_responsible: event.target.checked,
                          },
                        })
                      }
                      data-cy="switch-membership-responsible"
                    />
                  }
                  label="Planungsverantwortung"
                />
                <Tooltip title={blockedReason}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={Boolean(blockedReason)}
                      onClick={() =>
                        deleteMembership({
                          params: { user_id: user.id, organisation_guid: organisation.guid },
                        })
                      }
                      data-cy="btn-remove-membership"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )
        })}
        <AddMembershipForm
          userId={user.id}
          joinableOrganisations={joinableOrganisations}
          onAdded={invalidate}
        />
      </Box>
      <Button size="small" onClick={onClose}>
        Schließen
      </Button>
    </DialogBase>
  )
}

export default ManageMembershipsDialog
